import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Send, Mail, Loader2, ArrowRight, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useSiteContent } from "@/context/SiteContentContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ContactForm = () => {
  const { t } = useSiteContent();
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [lead, setLead] = useState("");
  const [leadLoading, setLeadLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submitContact = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/contact`, form);
      toast.success(data.message || "Message sent!");
      setForm({ name: "", email: "", phone: "", company: "", message: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail?.[0]?.msg || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const submitLead = async (e) => {
    e.preventDefault();
    if (!lead) { toast.error("Please enter your email."); return; }
    setLeadLoading(true);
    try {
      const { data } = await axios.post(`${API}/leads`, { email: lead });
      toast.success(data.message || "You're in!");
      setLead("");
    } catch (err) {
      toast.error("Please enter a valid email.");
    } finally {
      setLeadLoading(false);
    }
  };

  return (
    <section id="contact" className="relative py-28 px-6 hero-glow grain overflow-hidden" data-testid="contact-section">
      <div className="max-w-6xl mx-auto relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }} transition={{ duration: 0.6 }}
          className="text-center max-w-2xl mx-auto"
        >
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">
            {t('contact_title')} <span className="text-gradient">{t('contact_highlight')}</span>
          </h2>
          <p className="mt-4 text-white/55 text-lg">{t('contact_subtitle')}</p>
          <a
            data-testid="owner-email-link"
            href={`mailto:${t('owner_email')}`}
            className="mt-4 inline-flex items-center gap-2 text-indigo-400 hover:text-indigo-300 transition-colors font-medium"
          >
            <Mail className="h-4 w-4" /> {t('owner_email')}
          </a>
          {t('owner_phone') && (
            <a
              data-testid="owner-phone-link"
              href={`tel:${t('owner_phone').replace(/\s/g, "")}`}
              className="mt-2 inline-flex items-center gap-2 text-indigo-400 hover:text-indigo-300 transition-colors font-medium"
            >
              <Phone className="h-4 w-4" /> {t('owner_phone')}
            </a>
          )}
        </motion.div>

        {/* Email lead capture */}
        <motion.form
          onSubmit={submitLead}
          initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }} transition={{ duration: 0.5 }}
          className="mt-10 max-w-md mx-auto flex flex-col sm:flex-row gap-3"
          data-testid="lead-form"
        >
          <div className="relative flex-1">
            <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
            <Input
              data-testid="lead-email-input"
              type="email"
              placeholder="Enter your email for a free trial"
              value={lead}
              onChange={(e) => setLead(e.target.value)}
              className="pl-10 h-12 bg-white/5 border-white/15 text-white placeholder:text-white/40 rounded-full"
            />
          </div>
          <Button
            data-testid="lead-submit-button"
            type="submit"
            disabled={leadLoading}
            className="h-12 rounded-full bg-white text-black hover:bg-white/90 px-6 gap-2 border-0"
          >
            {leadLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Start Free Trial <ArrowRight className="h-4 w-4" /></>}
          </Button>
        </motion.form>

        {/* Contact form */}
        <motion.div
          initial={{ opacity: 0, scale: 0.97 }} whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }} transition={{ duration: 0.6 }}
          className="mt-16 max-w-2xl mx-auto glass rounded-3xl p-8 sm:p-10"
        >
          <h3 className="text-2xl font-semibold tracking-tight">Contact Sales</h3>
          <p className="text-white/55 mt-1.5">Tell us about your studio and we'll be in touch.</p>

          <form onSubmit={submitContact} className="mt-7 grid sm:grid-cols-2 gap-5" data-testid="contact-form">
            <div className="flex flex-col gap-2">
              <Label htmlFor="name" className="text-white/80">Name</Label>
              <Input data-testid="contact-name-input" id="name" value={form.name} onChange={update("name")}
                placeholder="Jane Doe" className="h-11 bg-white/5 border-white/15 text-white placeholder:text-white/35 rounded-xl" />
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="email" className="text-white/80">Email</Label>
              <Input data-testid="contact-email-input" id="email" type="email" value={form.email} onChange={update("email")}
                placeholder="jane@studio.com" className="h-11 bg-white/5 border-white/15 text-white placeholder:text-white/35 rounded-xl" />
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="phone" className="text-white/80">Teléfono <span className="text-white/40">(Opcional)</span></Label>
              <Input data-testid="contact-phone-input" id="phone" type="tel" value={form.phone} onChange={update("phone")}
                placeholder="+34 600 000 000" className="h-11 bg-white/5 border-white/15 text-white placeholder:text-white/35 rounded-xl" />
            </div>
            <div className="flex flex-col gap-2 sm:col-span-2">
              <Label htmlFor="company" className="text-white/80">Company <span className="text-white/40">(Optional)</span></Label>
              <Input data-testid="contact-company-input" id="company" value={form.company} onChange={update("company")}
                placeholder="Studio name" className="h-11 bg-white/5 border-white/15 text-white placeholder:text-white/35 rounded-xl" />
            </div>
            <div className="flex flex-col gap-2 sm:col-span-2">
              <Label htmlFor="message" className="text-white/80">Message</Label>
              <Textarea data-testid="contact-message-input" id="message" value={form.message} onChange={update("message")}
                placeholder="How can we help you scale?" rows={4}
                className="bg-white/5 border-white/15 text-white placeholder:text-white/35 rounded-xl resize-none" />
            </div>
            <Button
              data-testid="contact-submit-button"
              type="submit"
              disabled={loading}
              className="sm:col-span-2 h-12 rounded-full bg-gradient-to-r from-indigo-500 to-violet-500 hover:from-indigo-400 hover:to-violet-400 text-white gap-2 border-0 text-base"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4" /> Contact Sales</>}
            </Button>
          </form>
        </motion.div>
      </div>
    </section>
  );
};
